import { useCallback } from "react";
import { NavigateOptions, To, useNavigate } from "react-router-dom";
import { AppPageName } from "../constants/page-info";
import { pagePath } from "../constants/utils";

interface PageNavigateProps<T> {
  navigationType?: never;
  appPage: AppPageName[number];
  pathParams?: Record<string, string | number>;
  searchParams?: string | URLSearchParams;
  state?: T;
  replace?: boolean;
}
interface OriginalNavigateProps {
  navigationType: "original";
  TO_PROPS: { to: To; options?: NavigateOptions };
}
function isOriginalNavigateProps<T>(
  props: PageNavigateProps<T> | OriginalNavigateProps
): props is OriginalNavigateProps {
  return props.navigationType === "original";
}
export function usePageNavigate<T>() {
  const pageNavigate = useNavigate();

  function onNavigateCallBack(
    props: PageNavigateProps<T> | OriginalNavigateProps | number
  ) {
    if (typeof props === "number") {
      pageNavigate(props);
      return;
    }

    // Keeping Usage of Escape Hatch Ugly to discourage overusing this
    if (isOriginalNavigateProps(props)) {
      pageNavigate(props.TO_PROPS.to, props.TO_PROPS.options);
      return;
    }

    const searchParamsString = props.searchParams
      ? `?${new URLSearchParams(props.searchParams).toString()}`
      : "";

    const toPath =
      pagePath(props.appPage, props.pathParams) + searchParamsString;

    pageNavigate(toPath, { state: props.state, replace: props.replace });
  }

  return useCallback(onNavigateCallBack, [pageNavigate]);
}
